/* eslint-disable camelcase */
import { animated, easings, useSpring } from "@react-spring/web";
import classNames from "classnames";
import { MouseEventHandler, useRef } from "react";

// Components
import useTranslate from "hooks/useTranslate";
import Blick from "../Blick";
import Spin from "../Spin";

// Hooks

import "./styles.sass";

export type ButtonVariationType =
  | "primary"
  | "secondary"
  | "transparent"
  | "gradient"
  | "white"
  | "green"
  | "light-green"
  | "white-border"
  | "red"
  | "dark"
  | "chicks"
  | "dark-blue"
  | "blue"
  | "banger-gradient"
  | "glory-gradient"
  | "gradient-button-promo"
  | "orange";

type ButtonProps = {
  className?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  blick?: boolean;
  size?: "s" | "m";
  htmlType?: "button" | "submit";
  variation: ButtonVariationType;
  mode?: "light" | "dark";
  icon?: JSX.Element;
  onClick?: () => void;
  dataCy?: string;
  labelWithIcon?: JSX.Element;
  widthContent?: boolean;
};

const Button = (props: ButtonProps) => {
  const {
    className,
    label,
    disabled,
    loading,
    size = "m",
    htmlType = "button",
    variation = "primary",
    blick,
    icon,
    // eslint-disable-next-line unused-imports/no-unused-vars
    mode = import.meta.env.VITE_APP_PROJECT_NAME === "banger"
      ? "dark"
      : "light",
    onClick,
    dataCy,
    labelWithIcon,
    widthContent = false,
  } = props;

  const t = useTranslate();

  const btnRef = useRef<HTMLButtonElement>(null);

  const { x } = useSpring({
    from: {
      x: -100,
    },
    to: [{ x: 350 }, { x: -200 }],
    loop: true,
    config: { duration: 3000, easing: easings.easeInOutCubic },
  });

  const handleClick: MouseEventHandler = (event) => {
    event.stopPropagation();
    if (!disabled && onClick && !loading) {
      onClick();
    }
  };

  const isIconExist = icon && Object.keys(icon).length !== 0;

  const ButtonClass = classNames("Button", {
    [`Button__${variation}`]: variation,
    [`Button__${size}`]: size,
    Button__disabled: disabled,
    Button__loading: loading,
    "Button__no-label": !label,
    "Button__with-icon": !!isIconExist,
    "Button__width-content": !!widthContent,
    [`${className}`]: !!className,
  });

  if (loading) {
    return (
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <button
        // eslint-disable-next-line react/button-has-type
        type={htmlType}
        className={ButtonClass}
        disabled
        onClick={handleClick}
        ref={btnRef}
      >
        <Spin variation={variation} size="s" />
      </button>
    );
  }

  if (isIconExist) {
    return (
      <button
        // eslint-disable-next-line react/button-has-type
        type={htmlType}
        className={ButtonClass}
        onClick={handleClick}
        ref={btnRef}
      >
        {icon}
      </button>
    );
  }

  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={htmlType}
      className={ButtonClass}
      onClick={handleClick}
      ref={btnRef}
      data-cy={dataCy}
    >
      {blick && (
        <animated.div className="Button__blick" style={{ x }}>
          <Blick />
        </animated.div>
      )}
      {labelWithIcon}
      {t(label || "")}
    </button>
  );
};

export default Button;
